// Thin wrapper around gtag/dataLayer. Every event is auto-stamped with
// `platform: "web"` so GA4 funnels can filter web traffic out of the same
// property the mobile app (Firebase) reports into.
//
// Safe to call before gtag is ready or before consent is granted — the
// queue lives on window.dataLayer (set up in public/index.html) and
// Consent Mode v2 holds events back until the user accepts.

const PLATFORM = "web";

const hasDataLayer = () =>
  typeof window !== "undefined" && Array.isArray(window.dataLayer);

// ── PII guard ───────────────────────────────────────────────────────────
// Google's terms prohibit sending personally identifiable information to
// Google Analytics, and the penalty is deletion of the affected data — or
// the account. The GTM container maps a GA4 event parameter named `email`
// straight from the dataLayer, so any push carrying `email` ships the raw
// address to Google. That happened on the /dc and /nyc claim wizard.
//
// Strip these keys here rather than trusting every call site to remember,
// because the container mapping stays in place and will pick up anything
// that reaches the dataLayer under one of these names.
//
// If you need email-based conversion matching, that is Enhanced
// Conversions on the Ads side with hashed `user_data` — a deliberate,
// separate implementation. It is never a plain GA4 event parameter.
const PII_KEYS = [
  "email",
  "phone",
  "phone_number",
  "name",
  "first_name",
  "last_name",
  "full_name",
  "address",
  "street",
  "zip",
  "postal_code",
];

const stripPii = (params) => {
  const clean = {};
  const dropped = [];
  Object.keys(params).forEach((key) => {
    if (PII_KEYS.includes(key.toLowerCase())) {
      dropped.push(key);
      return;
    }
    clean[key] = params[key];
  });
  if (dropped.length && process.env.NODE_ENV !== "production") {
    console.warn(
      `[analytics] refused to send PII to GA4: ${dropped.join(", ")}. ` +
        "See PII_KEYS in src/utils/analytics.js.",
    );
  }
  return clean;
};

// Fire a custom event. Pushes a NAMED { event, ...params } object onto
// dataLayer — this is the only shape GTM recognises as a trigger.
// Using gtag("event", ...) instead would write the args positionally and
// GTM would silently ignore the custom name (you'd only see auto events
// like page_view / user_engagement / scroll land in GA4).
//
// Keep event names snake_case to match GA4 conventions and read
// consistently next to the mobile Firebase events.

// ── Conversion aliases ──────────────────────────────────────────────────
// TEMPORARY. The GTM container (GTM-KTJVK732) triggers all four of its
// Google Ads conversion tags on event names that predate the web_*
// convention used here — `sign_up`, `listing_published` and
// `web_booking_completed`. None of those names are emitted by this app,
// so none of those conversion tags have ever fired.
//
// Until the container triggers are repointed at the web_* names, mirror
// each conversion moment under the name the container is listening for.
//
// Remove an entry the moment its container trigger is updated. Leaving
// both in place once the trigger matches web_* would double-count.
//
// Note on booking: `web_booking_created` fires when the booking record is
// created, which for a paid booking is BEFORE Stripe has taken the money —
// aliasing it unconditionally would count abandoned payments as
// conversions. So a paid booking converts on payment confirmation, and a
// booking that requires no payment converts on creation.
// Note on listings: a host can publish a spot by two different routes and
// they emit different events. The authenticated host flow ends in
// `web_spot_created`. The city landing wizard (/dc, /nyc) bypasses that
// path entirely and ends in `web_claim_success`, fired once the spot is
// actually activated — `web_claim_spot_created` is a step advance that
// still precedes activation, and activation can fail. Both routes must
// alias or the landing pages, which is where paid acquisition lands,
// would report no listing conversions at all.
const CONVERSION_ALIASES = [
  { from: "web_signup_success", to: "sign_up" },
  { from: "web_spot_created", to: "listing_published" },
  { from: "web_claim_success", to: "listing_published" },
  { from: "web_payment_success", to: "web_booking_completed" },
  {
    from: "web_booking_created",
    to: "web_booking_completed",
    when: (params) => params.requires_payment === false,
  },
];

const pushToDataLayer = (eventName, params) => {
  window.dataLayer.push({
    event: eventName,
    platform: PLATFORM,
    ...stripPii(params),
  });
};

export const trackEvent = (eventName, params = {}) => {
  if (!eventName) return;
  if (!hasDataLayer()) {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    } else {
      return;
    }
  }
  pushToDataLayer(eventName, params);

  CONVERSION_ALIASES.forEach((alias) => {
    if (alias.from !== eventName) return;
    if (typeof alias.when === "function" && !alias.when(params)) return;
    pushToDataLayer(alias.to, params);
  });
};

// The last page_location we reported, used to collapse the duplicate
// initial page_view (see the guard inside trackPageView).
let lastTrackedLocation = null;

// Exposed for tests — module state persists across renders otherwise.
export const __resetPageViewDedupe = () => {
  lastTrackedLocation = null;
};

// SPA pageview — call on every route change. We pass page_location and
// page_path so GA4 picks them up the same way it would on a hard load.
export const trackPageView = (path) => {
  if (!hasDataLayer()) {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    } else {
      return;
    }
  }
  const resolvedPath =
    path ||
    (typeof window !== "undefined" && window.location
      ? window.location.pathname + window.location.search
      : "");
  const location =
    typeof window !== "undefined" && window.location
      ? window.location.href
      : resolvedPath;

  // ── Duplicate guard ───────────────────────────────────────────────────
  // initRouteTracking() fires the initial page_view itself AND patches
  // history.replaceState. react-router calls replaceState once while it
  // initialises, which ran the patched wrapper and produced a second,
  // identical page_view — two /g/collect hits with the same _p on every
  // single load, inflating page_view counts and distorting engagement.
  //
  // Suppress only a repeat of the URL we just reported. A real
  // navigation always changes the URL, and returning to the same URL
  // later still fires because lastTrackedLocation has moved on in
  // between. This is deliberately not time-based: react-router's
  // replaceState is not guaranteed to land inside any fixed window.
  if (location === lastTrackedLocation) return;
  lastTrackedLocation = location;

  window.dataLayer.push({
    event: "page_view",
    platform: PLATFORM,
    page_location: location,
    page_path: resolvedPath,
    page_title:
      typeof document !== "undefined" ? document.title : undefined,
  });
};

// Hooks once into history so every SPA navigation fires a page_view.
// Safe to call multiple times — guarded by a flag.
let historyPatched = false;
export const initRouteTracking = () => {
  if (typeof window === "undefined") return;
  if (historyPatched) return;
  historyPatched = true;

  // Fire the initial pageview (the GTM container fires its own on load too,
  // but we want ours to carry the platform stamp so the dataLayer-driven
  // tag and our tag agree). The replaceState react-router performs during
  // its own init would otherwise repeat this one — trackPageView's
  // duplicate guard collapses it.
  trackPageView();

  const wrap = (method) => {
    const original = window.history[method];
    window.history[method] = function patched(...args) {
      const result = original.apply(this, args);
      // Defer so document.title has updated.
      setTimeout(() => trackPageView(), 0);
      return result;
    };
  };
  wrap("pushState");
  wrap("replaceState");
  window.addEventListener("popstate", () => trackPageView());
};
